import React, { useState, useEffect } from 'react';
import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import EnquiryModal from '../components/landing/EnquiryModal';
import { useLocation } from 'react-router-dom';
import { Target, CheckCircle, Eye } from 'lucide-react';
import api from '../utils/axios';
import getFullUrl from '../utils/formatUrl';

const About = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [content, setContent] = useState({});
    const location = useLocation();

    const renderRole = (roleText) => {
        if (!roleText) return null;
        const lines = roleText.split('\n');
        return lines.map((line, lineIdx) => {
            const cleanedLine = line.replace(/\s+/g, ' ').trim();
            const regex = /(Arya\s+Sabhyata)/i;
            const match = cleanedLine.match(regex);
            if (match) {
                const matchIndex = cleanedLine.toLowerCase().indexOf('arya sabhyata');
                const titlePart = cleanedLine.substring(0, matchIndex).trim();
                const orgPart = cleanedLine.substring(matchIndex);
                
                const orgParts = orgPart.split(regex);
                
                return (
                    <div key={lineIdx} className={lineIdx > 0 ? "mt-1" : ""}>
                        {titlePart && (
                            <div className="uppercase mb-0.5">
                                {titlePart}
                            </div>
                        )}
                        <div>
                            {orgParts.map((part, partIdx) => {
                                if (part.toLowerCase() === 'arya sabhyata') {
                                    return (
                                        <span 
                                            key={partIdx} 
                                            className="text-[#cc0000] uppercase font-bold inline-block mr-1"
                                        >
                                            Arya Sabhyata
                                        </span>
                                    );
                                }
                                return (
                                    <span key={partIdx} className="uppercase">
                                        {part}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                );
            }
            return (
                <div key={lineIdx} className={lineIdx > 0 ? "mt-1 uppercase" : "uppercase"}>
                    {cleanedLine}
                </div>
            );
        });
    };

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        
        fetchContent();

        const hash = window.location.hash;
        if (hash) {
            const element = document.getElementById(hash.substring(1));
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        } else {
            window.scrollTo(0, 0);
        }

        return () => window.removeEventListener('scroll', handleScroll);
    }, [location]);

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => setIsInquiryOpen(true)}
                logo={content.site_logo}
                email={content.contact_email}
                phone={content.contact_phone}
                applyUrl={content.apply_url}
                colleges={content.colleges_list}
                placements={content.placements_list}
                research={content.research_list}
                journals={content.journals_list}
                innovations={content.innovations_list}
            />

            {/* Internal Hero */}
            <header className="relative h-[220px] md:h-[400px] bg-slate-900 flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-[#003366]/80 z-10"></div>
                <img 
                    src={getFullUrl(content.about_hero_image) || "https://images.unsplash.com/photo-1523050853064-dbad350e02ee?q=80&w=2000&auto=format&fit=crop"} 
                    className="absolute inset-0 w-full h-full object-cover"
                    alt="About Us"
                />
                <div className="max-w-7xl mx-auto px-4 relative z-20 w-full">
                    <h1 className="text-3xl sm:text-5xl md:text-6xl text-[#cc0000] tracking-tight" style={{ fontFamily: 'Forte' }}>
                        {content.about_hero_title || 'Arya Sabhyata Group'}
                    </h1>
                    <div className="h-1.5 w-24 bg-[#cc0000] mt-6"></div>
                </div>
            </header>

            {/* Heritage Section */}
            <section id="heritage" className="py-24 border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                        <div className="space-y-8">
                            <h3 className="text-[#cc0000] font-black uppercase tracking-[5px] text-[11px]">Our Heritage</h3>
                            <h2 className="text-4xl font-black uppercase text-slate-900 tracking-tight leading-tight">
                                {content.about_heritage_heading || 'A Legacy of Educational Excellence'}
                            </h2>
                            <p className="text-slate-600 leading-relaxed text-lg font-medium">
                                {content.about_heritage_description || 'Founded in 1998, Arya Sabhyata Group has been at the forefront of educational transformation in India. Our journey began with a single mission: to provide world-class technical education that is accessible and industry-relevant. Over two decades, we have evolved into a multi-disciplinary powerhouse, nurturing talent across engineering, management, and more.'}
                            </p>
                        </div>
                        <div className="relative lg:pl-10 mt-12 lg:mt-0">
                            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)] border-2 border-slate-50 flex items-start justify-center min-h-[400px] md:min-h-[500px] group overflow-hidden">
                                <img 
                                    src={getFullUrl(content.about_heritage_image) || "https://images.unsplash.com/photo-1541339907198-e08756ebafe1?auto=format&fit=crop&q=80&w=800"} 
                                    className="max-w-[100%] max-h-[280px] md:max-h-[400px] object-contain transition-transform duration-700 group-hover:scale-105" 
                                    alt="Heritage" 
                                />
                            </div>
                            <div className="absolute -bottom-6 md:-bottom-10 left-6 md:-left-10 bg-amber-400 p-5 md:p-8 rounded-2xl shadow-2xl border-t-8 border-[#003366] transform hover:scale-105 transition-transform z-20">
                                <div className="text-3xl md:text-4xl font-black text-[#003366] leading-none">{content.stats_years || '25'}+</div>
                                <div className="text-[9px] md:text-[10px] uppercase font-black text-[#003366]/70 tracking-widest mt-2">Years of Legacy</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Vision & Mission Section */}
            <section id="vision" className="py-24 bg-slate-900 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-1/4 h-full bg-[#cc0000]/10 -skew-x-12 translate-x-1/2"></div>
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
                        <div className="space-y-12">
                            <div className="flex items-start space-x-8">
                                <div className="p-4 bg-[#cc0000] rounded-sm">
                                    <Target size={32} strokeWidth={3} />
                                </div>
                                <div className="space-y-4">
                                    <h3 className="text-2xl font-black uppercase tracking-widest">Our Mission</h3>
                                    <p className="text-slate-400 font-medium leading-relaxed">
                                        {content.about_mission || 'To empower individuals through holistic education and innovative research, creating a sustainable impact on society and the global economy. We strive to be the bridge between aspiration and achievement.'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start space-x-8">
                                <div className="p-4 bg-amber-400 text-[#003366] rounded-sm">
                                    <Eye size={32} strokeWidth={3} />
                                </div>
                                <div className="space-y-4">
                                    <h3 className="text-2xl font-black uppercase tracking-widest">Our Vision</h3>
                                    <p className="text-slate-400 font-medium leading-relaxed">
                                        {content.about_vision || 'To be recognized as a premier global institution that fosters creativity, leadership, and ethical values in every student, shaping them into world-class professionals and responsible citizens.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white/5 p-12 border border-white/10 backdrop-blur-sm">
                            <h3 className="text-[#cc0000] font-black uppercase tracking-[5px] text-[11px] mb-8">Our Core Values</h3>
                            <ul className="space-y-6">
                                {[
                                    { title: 'Excellence', desc: 'Striving for the highest standards in everything we do.' },
                                    { title: 'Integrity', desc: 'Upholding honesty and transparency in our community.' },
                                    { title: 'Innovation', desc: 'Encouraging creative thinking and new-age solutions.' }
                                ].map((v, i) => (
                                    <li key={i} className="flex items-start space-x-4">
                                        <CheckCircle className="text-amber-400 mt-1" size={18} />
                                        <div>
                                            <h4 className="font-bold text-sm uppercase">{v.title}</h4>
                                            <p className="text-xs text-slate-500 mt-1">{v.desc}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Management Section */}
            <section id="management" className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center max-w-3xl mx-auto mb-20">
                        <h3 className="text-[#cc0000] font-black uppercase tracking-[5px] text-[11px] mb-4">Board of Governors</h3>
                        <h2 className="text-4xl font-black uppercase text-slate-900 tracking-tight">The Visionary Leadership</h2>
                        <p className="text-slate-500 font-medium mt-4">Our management team brings decades of experience from academia and industry to guide the group towards global benchmarks.</p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-12">
                        {( () => {
                            try {
                                const dynamicMembers = content.about_leadership_members ? JSON.parse(content.about_leadership_members) : [];
                                return dynamicMembers.length > 0 ? dynamicMembers : [
                                    { name: 'Dr. Satyam Palit', role: 'Chairman', image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=400' },
                                    { name: 'Prof. Anjali Roy', role: 'Managing Director', image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=400' },
                                    { name: 'Dr. Rahul Mukherjee', role: 'Executive Member', image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=400' }
                                ];
                            } catch(e) {
                                return [];
                            }
                        })().map((member, i) => (
                            <div key={i} className="group cursor-pointer w-full md:w-[350px]">
                                <div className="relative overflow-hidden rounded-2xl mb-6 shadow-2xl">
                                    <img 
                                        src={getFullUrl(member.image) || member.image || "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=400"} 
                                        className="w-full h-[450px] object-cover transition-all duration-700 group-hover:scale-105" 
                                        alt={member.name} 
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80"></div>
                                    <div className="absolute bottom-6 left-6 text-white text-left">
                                        <h4 className="text-xl font-black uppercase tracking-tight text-white mb-1">{member.name || 'Leadership Member'}</h4>
                                        <p className="text-white/80 font-bold text-[10px] tracking-[3px] border-t border-white/20 pt-2 inline-block">{renderRole(member.role || 'Board Member')}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer data={content} />

            <EnquiryModal isOpen={isInquiryOpen} onClose={() => setIsInquiryOpen(false)} />
        </div>
    );
};

export default About;
